import axios from 'axios'
import { SfuNode } from '../UserContext'
import { chunkArray } from '../utils'
import * as Constants from '../services/Constants'
import { logger } from './Logger'
import { env } from './EnvironmentVariables'

const log = logger.createChildLogger({ name: 'SfuNodeSelection' })

const timedAxios = axios.create()

timedAxios.interceptors.request.use((config) => {
    config.headers['request-startTime'] = new Date().getTime()
    return config
})

timedAxios.interceptors.response.use((response) => {
    const currentTime = new Date().getTime()
    const startTime = response.config.headers['request-startTime']
    response.headers['request-duration'] = currentTime - startTime
    return response
})

function measureLatencyOfNode(n: Server, statusPath: string | null): Promise<{ value?: number; error?: string }> {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve({ error: 'timed_out' })
        }, Constants.data.node_latency_measurement_timeout_ms)

        try {
            const u = new URL(n.url)
            u.protocol = 'https:'
            if (statusPath) {
                u.pathname = statusPath
            }
            u.port = ''
            timedAxios
                .get(u.toString())
                .then((response) => {
                    const latency = response.headers['request-duration']
                    resolve({ value: latency })
                })
                .catch((error) => {
                    resolve({ error: 'network_error' })
                })
        } catch (e) {
            resolve({ error: 'exception' })
        }
    })
}

interface Server {
    url: string
}

type LatencyAnnotation = {
    latency: number | null
    latencyError?: string
}

export class ServerLatencyAnnotator<T extends Server> {
    nodes: (T & LatencyAnnotation)[] = []
    busyPromise: Promise<T & LatencyAnnotation> | null = null
    nearestNode: (T & LatencyAnnotation) | null = null
    statusPath: string | null = null

    constructor(statusPath: string | null = null) {
        this.statusPath = statusPath
    }

    async setAvailableNodes(nodes: T[]): Promise<T & LatencyAnnotation> {
        if (this.nearestNode) {
            return this.nearestNode
        }
        if (this.busyPromise) {
            return this.busyPromise
        }

        return (this.busyPromise = new Promise(async (resolve, reject) => {
            let resultArray: { value?: number; error?: string }[] = []
            const batches = chunkArray(nodes, 5)

            for (let batch of batches) {
                let resultArrayBatch = (await Promise.all(
                    batch.map((n) => measureLatencyOfNode(n, this.statusPath))
                )) as {
                    value?: number
                    error?: string
                }[]
                resultArray = resultArray.concat(resultArrayBatch)
            }

            const annotatedNodes: (T & LatencyAnnotation)[] = resultArray.map((v, i) => {
                if (v.error) {
                    return { ...nodes[i], latency: null, latencyError: v.error }
                }
                return { ...nodes[i], latency: v.value ?? null }
            })

            const M = Number.MAX_SAFE_INTEGER
            annotatedNodes.sort((a, b) => ((a.latency || M) < (b.latency || M) ? -1 : 1))

            this.nearestNode = annotatedNodes[0] || null
            this.nodes = annotatedNodes

            this.busyPromise = null
            resolve(annotatedNodes[0])
        }))
    }

    getNodes() {
        return this.nodes
    }

    getNearestNode(): Promise<(T & LatencyAnnotation) | null> {
        return new Promise((resolve, reject) => {
            if (env.TEST_SFU_GET_NEAREST_NODE_EXCEPTION) {
                return resolve(null)
            }

            if (this.nearestNode) {
                resolve(this.nearestNode)
                return
            }

            let timeout: NodeJS.Timeout | undefined = undefined

            setTimeout(() => {
                clearInterval(timeout)

                // return fallback node
                if (this.nodes[0]) {
                    resolve(this.nodes[0])
                } else {
                    return resolve(null)
                }
            }, 3000)

            // poll until `this.nearestNode` becomes available
            // or timeout (see above)
            timeout = setInterval(() => {
                if (this.nearestNode) {
                    clearInterval(timeout)
                    resolve(this.nearestNode)
                }
            }, 250)
        })
    }
}

export const sfuNodeSelection = new ServerLatencyAnnotator<SfuNode>()
